import type { ReactNode } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { LoadingBlock } from "@/widgets/LoadingBlock"
import { Alert } from "@/shared/ui/Alert"
import { Button } from "@/shared/ui/Button"
import type { UserRole } from "@/shared/api/types"
import { useNbrAuth } from "./useNbrAuth"
import { loginPath } from "./returnTo"

/** Requires authentication and preserves only a validated internal return path. */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const auth = useNbrAuth()
  const location = useLocation()

  if (auth.error) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Alert tone="error">{t("common.auth.error", { message: auth.error.message })}</Alert>
        <Button className="mt-4" onClick={() => auth.login()}>
          {t("nav.login")}
        </Button>
      </div>
    )
  }

  if (auth.isLoading) return <LoadingBlock variant="page" message={t("common.loading")} />
  if (!auth.isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={loginPath(returnTo)} replace />
  }

  return <>{children}</>
}

/** Требует одну из ролей (после аутентификации). */
export const RequireRole = ({ role, children }: { role: UserRole | UserRole[]; children: ReactNode }) => {
  const { t } = useTranslation()
  const auth = useNbrAuth()

  return (
    <RequireAuth>
      {auth.hasRole(role) ? (
        children
      ) : (
        <div className="mx-auto max-w-2xl px-6 py-16">
          <Alert tone="warning">{t("common.auth.forbidden")}</Alert>
        </div>
      )}
    </RequireAuth>
  )
}
